'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import apiClient from '@/lib/api';
import { Article, Statistics } from '@/types';

export default function HomePage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [stats, setStats] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesResponse, statsResponse] = await Promise.all([
          apiClient.getArticles(),
          apiClient.getStats()
        ]);
        setArticles(articlesResponse);
        setStats(statsResponse);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error loading data');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Animated Stars Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="stars-container">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Hero Section - Grok.com Style */}
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 relative z-10">
        {/* Logo */}
        <div className="flex items-center space-x-3 mb-8">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
            <span className="text-black font-bold text-2xl">G</span>
          </div>
          <span className="text-4xl md:text-5xl font-bold text-white">Grokypedia</span>
        </div>

        {/* Main Search Bar */}
        <div className="w-full max-w-2xl mb-8">
          <form onSubmit={(e) => {
            e.preventDefault();
            if (searchQuery.trim()) {
              window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
            }
          }} className="relative">
            <input
              type="text"
              placeholder="What do you want to know?"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-4 text-lg bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-gray-500 transition-colors"
              dir="ltr"
            />
            <button
              type="submit"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </form>
        </div>

        {/* Quick Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            href="/contribute"
            className="grok-btn-primary px-6 py-3 text-lg font-medium"
          >
            Contribute Knowledge
          </Link>
          <Link 
            href="/about"
            className="grok-btn-secondary px-6 py-3 text-lg font-medium"
          >
            Learn More
          </Link>
        </div>
      </div>

      {/* Featured Articles */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">Featured Articles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.slice(0, 6).map((article) => (
            <Link 
              key={article._id} 
              href={`/wiki/${encodeURIComponent(article.title)}`}
              className="grok-card p-6"
            >
              <h3 className="text-xl font-semibold text-white mb-3">
                {article.title}
              </h3>
              <p className="text-gray-300 mb-4 line-clamp-3">
                {article.summary}
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {(article.categories || []).slice(0, 3).map((category, index) => (
                  <span 
                    key={category._id || `${article._id}-category-${index}`}
                    className="bg-gray-700 text-gray-200 text-sm px-2 py-1 rounded"
                  >
                    {category.name}
                  </span>
                ))}
              </div>
              <div className="flex justify-between items-center text-sm text-gray-400">
                <span>{article.viewsCount} views</span>
                {article.qualityScore && (
                  <span className="flex items-center">
                    ⭐ {article.qualityScore}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">Categories</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats?.categories && stats.categories.length > 0 ? (
            stats.categories.map((category) => (
              <Link 
                key={category._id}
                href={`/category/${encodeURIComponent(category.slug)}`}
                className="grok-card p-6 text-center"
              >
                <div className="text-4xl mb-2">{category.icon || '📄'}</div>
                <h3 className="font-semibold text-white mb-1">{category.name}</h3>
                <p className="text-sm text-gray-400">{category.articleCount} articles</p>
              </Link>
            ))
          ) : (
            // Fallback static categories if no dynamic data
            [
              { name: 'Technology', icon: '💻', count: 45 },
              { name: 'Science', icon: '🔬', count: 32 },
              { name: 'History', icon: '📚', count: 28 },
              { name: 'Culture', icon: '🎭', count: 19 },
              { name: 'Sports', icon: '⚽', count: 15 },
              { name: 'Geography', icon: '🌍', count: 23 },
              { name: 'Art', icon: '🎨', count: 12 },
              { name: 'Mathematics', icon: '🔢', count: 18 }
            ].map((category) => (
              <Link 
                key={category.name}
                href={`/category/${encodeURIComponent(category.name)}`}
                className="grok-card p-6 text-center"
              >
                <div className="text-4xl mb-2">{category.icon}</div>
                <h3 className="font-semibold text-white mb-1">{category.name}</h3>
                <p className="text-sm text-gray-400">{category.count} articles</p>
              </Link>
            ))
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gray-900 rounded-xl p-8 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Grokypedia in Numbers
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-white">
                {stats ? stats.totalArticles.toLocaleString() : '...'}
              </div>
              <div className="text-gray-400">Articles</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">
                {stats ? stats.activeEditors.toLocaleString() : '...'}
              </div>
              <div className="text-gray-400">Active Editors</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">
                {stats ? (stats.totalViews >= 1000 ? `${(stats.totalViews / 1000).toFixed(1)}K` : stats.totalViews.toLocaleString()) : '...'}
              </div>
              <div className="text-gray-400">Total Views</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">
                {stats ? stats.totalEditings.toLocaleString() : '...'}
              </div>
              <div className="text-gray-400">Total Editings</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}